<template>
  <page layoutClass="manage-emailsender" :loading="!recipients || isLoading">

    <b-row class="mb-3">

      <b-col cols="12" md="6">
        <b-row class="mb-3">
          <b-col cols="12" sm="4">
            <h1 class="m-0">Users</h1>
          </b-col>
          <b-col cols="12" sm="8" class="d-flex flex-row justify-content-end align-items-center">
            <b-button class="mr-3" size="md" variant="outline-primary" @click.prevent="() => showFilters = !showFilters">
              <font-awesome-icon icon="filter"/>
              <span class="d-none d-sm-inline-block">Filter</span>
            </b-button>

            <b-card class="shadow" body-class="d-flex flex-row justify-content-between align-items-center p-1">
              <b-form-select :options="segmentSelectorOpts" v-model="segmentWhere"/>
            </b-card>
          </b-col>
        </b-row>

        <b-card class="shadow mb-3" v-if="showFilters">
          <boolean-search :fields="searchFields" :value="filterWhere || {}" @input="value => value && Object.keys(value).length ? filterWhere = value : filterWhere = null"/>
        </b-card>

        <b-row class="mb-3">
          <b-col cols="12">
            <b-alert dismissible fade :show="showAllSelectedAlert" @dismissed="showAllSelectedAlert=false">You have selected <strong>{{selectedItems.length}}</strong> users.</b-alert>

            <b-card class="shadow p-0">
              <b-table table-class="m-0" hover striped small responsive show-empty
                :fields="fields" :items="items" :per-page="limit" :current-page="page"
                :busy="$apollo.queries.recipients.loading">
                  <template #table-busy>
                    <div class="text-center my-2">
                      <b-spinner class="align-middle"></b-spinner>
                      <strong>Loading...</strong>
                    </div>
                  </template>
                  <template #head(selected)>
                    <b-form-checkbox :checked="isAllSelected" @change="toggleAllSelected"/>
                  </template>
                  <template #cell(selected)="{ item: { __typename, id } }">
                    <b-form-checkbox :checked="selectedItems.includes(`${__typename}:${id}`)" @change="toggleSelected(`${__typename}:${id}`)"/>
                  </template>
                  <template #cell(avatar)="{ item }">
                    <avatar :user="item" :size="30"/>
                  </template>
              </b-table>

              <div class="d-flex flex-row p-3 justify-content-center align-items-center p-2"
                v-if="!$apollo.queries.recipients.loading && items.length > limit">
                <b-pagination class="m-0" v-model="page" :per-page="limit" :total-rows="items.length"/>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-col>

      <b-col cols="12" md="6">
          <b-row>
            <b-col cols="6">
              <h1 class="m-0">Email</h1>
            </b-col>
            <b-col cols="6">
              <b-card class="shadow" body-class="d-flex flex-row justify-content-between align-items-center p-1">
                <b-form-select :options="templates" v-model="templateKey"/>
              </b-card>
            </b-col>
          </b-row>
          <div class="pt-3">
          <b-card class="shadow p-0" style="z-index: 0" footer-class="email-composer-footer">

            <b-list-group flush>
              <b-list-group-item>
                <b-row>
                  <b-col cols="3" class="pt-2">
                    <strong>From Name:</strong>
                  </b-col>
                  <b-col cols="9">
                    <b-form-input v-model="fromName" placeholder="From name..." />
                  </b-col>
                </b-row>
              </b-list-group-item>
              <b-list-group-item>
                <b-row>
                  <b-col cols="3" class="pt-2">
                    <strong>Campaign ID:</strong>
                  </b-col>
                  <b-col cols="9">
                    <b-form-input v-model="campaign" placeholder="Campaign..." />
                  </b-col>
                </b-row>
              </b-list-group-item>
              <b-list-group-item>
                <b-row>
                  <b-col cols="3" class="pt-2">
                    <strong>To:</strong>
                  </b-col>
                  <b-col cols="9">
                    <template v-if="selectedItems.length">
                      {{ selectedItems.length | plural('recipient') }} selected
                      <span class="email-tag-expand" @click="() => selectedItems = []">clear all</span>
                    </template>
                    <span v-else class="to-line-placeholder">No one is selected</span>
                  </b-col>
                </b-row>
              </b-list-group-item>
              <b-list-group-item>
                <b-row>
                  <b-col cols="3" class="pt-2">
                    <strong>Subject:</strong>
                  </b-col>
                  <b-col cols="9">
                    <b-form-input v-model="subject" require placeholder="Email subject..." />
                  </b-col>
                </b-row>
              </b-list-group-item>
              <b-list-group-item v-for="({ type, name, content }, index) in slots" :key="`${templateKey}-key-${index}`">
                <b-row>
                  <b-col cols="3" class="pt-2">
                    <strong>{{ name || `Slot #${index+1}`}}:</strong>
                  </b-col>
                  <b-col cols="9">
                    <TiptapEditor v-if="type === 'html'" :ref="`${templateKey}-editor-${index}`"
                      :value="content" @input="value => slots[index].content = value"/>
                    <b-form-input v-if="type === 'input'" :ref="`${templateKey}-editor-${index}`"
                      :value="content" @input="value => slots[index].content = value"/>
                  </b-col>
                </b-row>
              </b-list-group-item>
              <b-list-group-item>
                <div class="variables-box text-muted">
                  <strong>Variables you can use:</strong>
                  <span><pre class="d-inline text-secondary m-0 pr-1">{{'user.firstName' | tokenVar}}</pre> - User's first name</span>
                  <span><pre class="d-inline text-secondary m-0 pr-1">{{'user.fullName' | tokenVar}}</pre> - User's full name</span>
                  <span><pre class="d-inline text-secondary m-0 pr-1">{{'user.emailAddress' | tokenVar}}</pre> - User's email address</span>
                  <span><pre class="d-inline text-secondary m-0 pr-1">{{'user.title' | tokenVar}}</pre> - User's job title</span>
                  <span><pre class="d-inline text-secondary m-0 pr-1">{{'company.name' | tokenVar}}</pre> - Company's name ({{companyName}})</span>
                </div>
              </b-list-group-item>

              <!-- Branding Settings -->
              <b-list-group-item>
                <b-button
                  variant="link"
                  @click="showBrandingSettings = !showBrandingSettings"
                >{{ showBrandingSettings ? 'Hide' : 'Show' }} Branding Settings</b-button>
              </b-list-group-item>
              <template v-if="showBrandingSettings">
                <b-list-group-item>
                  <b-row>
                    <b-col cols="3" class="pt-2">
                      <strong>Header Image:</strong>
                    </b-col>
                    <b-col cols="9">
                      <b-form-input v-model="brandingSettings.headerImage" placeholder="http://..." />
                      <b-form-checkbox class="mt-3" v-model="brandingSettings.hideBrandLogo">
                        Hide the logo in favour of the Header image
                      </b-form-checkbox>
                    </b-col>
                  </b-row>
                </b-list-group-item>
                <b-list-group-item>
                  <b-row>
                    <b-col cols="3" class="pt-2">
                      <strong>Primary Colour:</strong>
                    </b-col>
                    <b-col cols="9">
                      <chrome-picker
                        disable-alpha
                        :value="brandingSettings.primaryColour"
                        @input="({ hex }) => brandingSettings.primaryColour = hex || null"
                      />
                    </b-col>
                  </b-row>
                </b-list-group-item>
                <b-list-group-item>
                  <b-row>
                    <b-col cols="3" class="pt-2">
                      <strong>Secondary Colour:</strong>
                    </b-col>
                    <b-col cols="9">
                      <chrome-picker
                        disable-alpha
                        :value="brandingSettings.secondaryColour"
                        @input="({ hex }) => brandingSettings.secondaryColour = hex || null"
                      />
                    </b-col>
                  </b-row>
                </b-list-group-item>
                <b-list-group-item>
                  <b-row>
                    <b-col cols="3" class="pt-2">
                      <strong>Background Colour:</strong>
                    </b-col>
                    <b-col cols="9">
                      <chrome-picker
                        disable-alpha
                        :value="brandingSettings.backgroundColour"
                        @input="({ hex }) => brandingSettings.backgroundColour = hex || null"
                      />
                    </b-col>
                  </b-row>
                </b-list-group-item>
              </template>
            </b-list-group>

            <template v-slot:footer>
              <div class="d-flex flex-row justify-content-end">
                <template v-if="`${templateKey}`.startsWith('saved:')">
                  <b-button class="mx-2" size="md" variant="link" @click="saveExistingEmailTemplate">
                    Save
                  </b-button>
                  <b-button class="mx-2" size="md" variant="link" @click="saveNewEmailTemplate">
                    Save As
                  </b-button>
                  <b-button class="text-danger mx-2" size="md" variant="link" @click="removeSavedEmailTemplate">
                      Delete
                  </b-button>
                </template>
                <template v-else-if="templateKey === 'generic'">
                  <b-button class="mx-2" size="md" variant="link" @click="saveNewEmailTemplate">
                    Save
                  </b-button>
                </template>

                <div class="flex-fill"/>

                <b-button class="mx-2" size="md" variant="link" @click="preview">
                  Preview
                </b-button>

                <b-button class="mx-2" size="md" variant="link" @click="sendTestEmail">
                  Send Test
                </b-button>

                <b-button class="mx-2" size="md" variant="primary" v-b-modal="'areYouSureAlertModal'" :disabled="!selectedItems.length || !subject">
                  Send
                </b-button>
              </div>
            </template>

          </b-card>
        </div>
      </b-col>

    </b-row>

    <b-modal body-class="p-0 text-center" id="previewModal" hide-footer size="lg">
      <template v-slot:modal-header="{ close }">
        <h5 class="modal-title">Email Preview</h5>
        <div class="flex-fill"/>
        <button class="close d-none d-sm-inline-block" @click="() => previewType = 'Desktop'" type="button" aria-label="Preview Desktop">
          <font-awesome-icon icon="desktop"/>
        </button>
        <button class="close d-none d-sm-inline-block" @click="() => previewType = 'Mobile'" type="button" aria-label="Preview Mobile">
          <font-awesome-icon icon="mobile-alt"/>
        </button>
        <button class="close" @click="close" type="button" aria-label="Close">×</button>
      </template>

      <loading-spinner v-if="previewState === 'LOADING'" class="m-5"/>
      <b-alert v-else-if="previewState === 'ERROR'" class="m-5" show variant="danger">
        An error occurred rendering your email preview. Please close this popup &amp; try again.<br/>
        If this error persists please notify us at <a href="mailto:support@reallinks.io">support@reallinks.io</a>.
      </b-alert>
      <iframe
        :width="previewType === 'Desktop' ? '100%' : '340px'"
        height="500px"
        v-else-if="emailHTML"
        :srcdoc="emailHTML"
        class="border-0 mx-auto"
      ></iframe>
    </b-modal>

    <b-modal id="areYouSureAlertModal" title="Sending Emails">
      <p class="text-dark">Are you sure you want to send this email?</p>
      <b-form-checkbox v-model="confirmed">
        I understand that this email will be sent to {{selectedItems.length}} recipient{{selectedItems.length > 1 ? 's' : ''}}
      </b-form-checkbox>
      <template v-slot:modal-footer>
        <b-button class="mt-3" variant="danger" @click="$bvModal.hide('areYouSureAlertModal')">Cancel</b-button>
        <b-button class="mt-3 primary" variant="primary" @click="sendEmails()" :disabled="!confirmed">Send</b-button>
      </template>
    </b-modal>

    <b-modal ref="emailSentSucessfullyModal" id="emailSentSucessfullyModal" title="Emails Sent Successfully" body-class="py-5" hide-header>
      <div class="p-3">
        <p class="feedback-icon-line success"><font-awesome-icon icon="check"/></p>
        <p class="text-dark feedback-text-line">The email has been successfully sent to {{selectedItems.length}} recipient{{selectedItems.length > 1 ? 's' : ''}}.</p>
      </div>
      <template v-slot:modal-footer>
        <b-button class="primary" variant="primary" @click="$bvModal.hide('emailSentSucessfullyModal')">OK</b-button>
      </template>
    </b-modal>

    <b-modal ref="testEmailSentSucessfullyModal" id="testEmailSentSucessfullyModal" title="Test Email Sent Successfully" body-class="py-5" hide-header>
      <p class="feedback-icon-line success"><font-awesome-icon icon="check"/></p>
      <p class="text-dark feedback-text-line">The email has been successfully sent to your email address.</p>
      <template v-slot:modal-footer>
        <b-button class="primary" variant="primary" @click="$bvModal.hide('testEmailSentSucessfullyModal')">OK</b-button>
      </template>
    </b-modal>

    <b-modal ref="emailSendErrorModal" id="emailSendErrorModal" title="Error Sending Emails" body-class="py-5" hide-header>
      <p class="feedback-icon-line error"><font-awesome-icon icon="times"/></p>
      <p class="text-dark feedback-text-line">Unfortunately, we coud not send your email.</p>
      <template v-slot:modal-footer>
        <b-button class="primary" variant="primary" @click="$bvModal.hide('emailSendErrorModal')">OK</b-button>
      </template>
    </b-modal>

  </page>
</template>

<script>
import _sortBy from 'lodash/sortBy';
import _startCase from 'lodash/startCase';
import gql from 'graphql-tag';
import slugify from 'slugify';
import Page from '@/components/Page';
import Avatar from '@/components/users/Avatar';
import { Chrome as ChromePicker } from 'vue-color';
import enumsMixin from '@/mixins/enums';
import ErrorBlock from '@/components/misc/ErrorBlock';
import SendEmailMutation from '@/graphql/mutations/SendEmail.gql';
import BooleanSearch from '@/components/misc/BooleanSearch';
import TiptapEditor from '@/components/misc/TiptapEditor';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import { mapRoot } from '@src/mixins/root';
import templateList from '@/emails';

const FetchDataQuery = gql`
  query EmailSenderData {
    company {
      id

      emailManualTemplates

      leaderboards(where: { status: ACTIVE }, order: CREATED_ASC) {
        id
        name
        type
        raffleMode

        membership {
          everyone
          departments {
            id
          }
          users {
            id
          }
        }

        prizes {
          first {
            title
            image {
              id
              fileLocation
            }
          }
          second {
            title
            image {
              id
              fileLocation
            }
          }
          third {
            title
            image {
              id
              fileLocation
            }
          }
        }
      }

      userSearchFields: customFields(where: { status: ACTIVE }) {
        key
        name
        description
        type
        options
      }
    }
  }
`;

const ListUsersQuery = gql`
  query EmailSenderListUsers(
    $includeUsers: Boolean = false,
    $whereUsers: UserWhereManyInput = { status: ACTIVE },
    $includeInvites: Boolean = false,
    $whereInvites: UserInviteWhereManyInput = { status: INVITED },
  ) {
    company {
      id

      users(where: $whereUsers, order: CREATED_ASC) @include(if: $includeUsers) {
        id
        name: fullName
        emailAddress
        role

        departments {
          id
          name
        }

        customFields {
          key
          value
        }
      }

      userInvites(where: $whereInvites, order: CREATED_ASC) @include(if: $includeInvites) {
        id
        name
        emailAddress
      }
    }
  }
`;

const renderEmailMutation = gql`
  mutation RenderEmail($template: String!,$data: String) {
    renderEmail(template: $template, data: $data)
  }
`;

const emailQuery = /* GraphQL */`
  query CustomTemplateQuery {
    company {
      id
      name
      domain(where: { primary: true }) {
        domain(prefix:"https://")
        emailFrom
      }
      branding {
        name
        bigLogo {
          id
          fileLocation
        }
        smallLogo {
          id
          fileLocation
        }
        emailHeaderImage {
          id
          fileLocation
        }
        headerFont
        bodyFont
        primaryColour
        secondaryColour
        backgroundColour
      }
    }
    user {
      id
      firstName
      fullName
      emailAddress
      title
    }
  }
`;

const saveEmailTemplates = gql`
  mutation SaveEmailSenderTemplates($templates: KeyValueHash) {
    updateCompany(update: { emailManualTemplates: $templates }) {
      emailManualTemplates
    }
  }
`;

export default {
  components: {
    Page,
    ErrorBlock,
    BooleanSearch,
    LoadingSpinner,
    TiptapEditor,
    ChromePicker,
    Avatar,
  },
  mixins: [
    enumsMixin,
  ],
  data: () => ({
    selectedItems: [],
    page: 1,
    limit: 25,
    showAllSelectedAlert: false,
    showFilters: false,
    filterWhere: null,
    fromName: '',
    campaign: '',
    subject: templateList[0].subject,
    showBrandingSettings: false,
    brandingSettings: {},
    defaultBrandingSettings: {},
    confirmed: false,
    isLoading: false,
    templateKey: templateList[0].value,
    slots: templateList[0].slots.map(({ name, type, defaultContent }) => ({ name, type, content: defaultContent })),
    usersContext: templateList[0].usersContext || null,
    segmentSelectorOpts: DEFAULT_SEGMENT_OPTS,
    segmentWhere: null,
    emailHTML: null,
    previewState: null,
    previewType: 'Desktop',
    emailManualTemplates: null,
    departments: [],
    leaderboards: [],
    userSearchFields: [],
  }),
  mounted() {
    const { name: companyName, siteBranding, sitePreferences } = this.company || {};
    const { emailHeaderImage, primaryColour, secondaryColour, backgroundColour, headerFont, bodyFont } = siteBranding || {};
    const { emailHideBrandLogo } = sitePreferences || {};

    this.subject = `A message from ${companyName}`;
    this.defaultBrandingSettings.headerImage = emailHeaderImage ? emailHeaderImage.fileLocation : '';
    this.defaultBrandingSettings.hideBrandLogo = emailHideBrandLogo === true;
    this.defaultBrandingSettings.primaryColour = primaryColour;
    this.defaultBrandingSettings.secondaryColour = secondaryColour;
    this.defaultBrandingSettings.backgroundColour = backgroundColour;
    this.defaultBrandingSettings.bodyFont = bodyFont;
    this.defaultBrandingSettings.headerFont = headerFont;

    this.brandingSettings = { ...this.defaultBrandingSettings };
  },
  apollo: {
    data: {
      query: FetchDataQuery,
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { departments, leaderboards, emailManualTemplates, userSearchFields } = data.company;

          this.departments = Array.isArray(departments) && departments.length ? departments : [];
          this.leaderboards = (Array.isArray(leaderboards) && leaderboards.length ? leaderboards : []).map(leaderboard => {
            const { everyone, departments: lbds, users: lbus } = leaderboard.membership || {};
            return {
              ...leaderboard,
              membership: undefined,
              ...(everyone === true ? { everyone: true } : {
                departmentIDs: Array.isArray(lbds) ? lbds.map(({ id }) => id) : null,
                userIDs: Array.isArray(lbus) ? lbus.map(({ id }) => id) : null,
              }),
            };
          });

          this.emailManualTemplates = { ...emailManualTemplates };
          for (const key in this.emailManualTemplates) {
            if (this.emailManualTemplates.hasOwnProperty(key)) {
              this.emailManualTemplates[key] = JSON.parse(this.emailManualTemplates[key]);
            }
          }

          this.userSearchFields = (Array.isArray(userSearchFields) ? userSearchFields : []).filter(field => {
            const { type, options } = field || {};
            // return type === 'STRING' || (type === 'ENUM' && options);
            return type === 'ENUM' && options;
          });
        }
      },
      error(err) {
        this.$notifyFatalErr(err);
      },
      fetchPolicy: 'network-only',
      manual: true,
    },
    recipients: {
      query: ListUsersQuery,
      variables() {
        const { segmentWhere, usersContext } = this;
        if (usersContext === 'LEADERBOARDS') {
          return { includeUsers: true };
        } else {
          return segmentWhere || { includeUsers: true, includeInvites: true };
        }
      },
      update: ({ company: { users, userInvites } }) => ([
        ...(Array.isArray(users) ? users : []).filter(user => {
          const { id, role } = user || {};
          return id && role !== 'SUPER_ADMIN';
        }),
        ...(Array.isArray(userInvites) ? userInvites : []).map(invite => ({
          ...invite,
          role: 'INVITED',
        })),
      ]),
      result({ loading, data }) {
        if (!loading && data) {
          this.filterWhere = {};
          this.page = 1;
        }
      },
      error(err) {
        this.$notifyFatalErr(err);
      },
      fetchPolicy: 'network-only',
    },
  },
  filters: {
    tokenVar: string => `{{${string}}}`,
  },
  computed: {
    ...mapRoot([ 'user', 'company', 'isRecruiterAdmin' ]),
    companyName() {
      const { name, siteBranding: { name: brandingName } = {} } = this.company || {};
      return brandingName || name || 'Referrals';
    },
    templates() {
      let list = templateList.map(({ text, value }) => ({ text, value }));

      if (this.emailManualTemplates) {
        list = list.concat(Object.keys(this.emailManualTemplates).map(key => {
          const { name } = this.emailManualTemplates[key];
          return { text: `${name} (Saved)`, value: `saved:${key}` };
        }));
      }

      return list;
    },
    fields() {
      const { userRoleEnums } = this;
      if (this.$apollo && this.$apollo.queries && this.$apollo.queries.recipients.loading) {
        return [ { key: 'selected', label: '', thStyle: { width: '30px' } } ];
      }

      return [
        {
          key: 'selected',
          label: '',
          headerTitle: 'Selected',
          thStyle: { width: '30px' },
        },
        {
          key: 'name',
          label: 'Name',
        },
        {
          key: 'emailAddress',
          label: 'Email',
        },
        {
          key: 'role',
          label: 'Role',
          formatter: value => userRoleEnums[value] || _startCase(`${value}`.toLowerCase()),
        },
        {
          key: 'departments',
          label: 'Departments',
          formatter: values => (Array.isArray(values) ? values : []).map(({ name }) => name).join(', '),
        },
        // ...(Array.isArray(this.userSearchFields) ? this.userSearchFields : []).map(field => {
        //   const { key, name: label } = field;
        //   return {
        //     key: 'customFields',
        //     label,
        //     formatter(v, k, user) {
        //       return (user && Array.isArray(user.customFields))
        //         ? (user.customFields.find(f => f && f.key === key) || {}).value || null
        //         : null;
        //     },
        //   };
        // }),
      ];
    },
    items() {
      const { filterWhere, segmentWhere, usersContext } = this;
      let recipients = Array.isArray(this.recipients) ? this.recipients : [];

      if (usersContext === 'LEADERBOARDS' && typeof segmentWhere === 'string') {
        const leaderboard = this.leaderboards.find(l => l.id === segmentWhere);
        this.$assert(leaderboard && leaderboard.id, 'Leaderboard not found');
        const { departmentIDs, userIDs } = leaderboard;
        if (Array.isArray(departmentIDs) || Array.isArray(userIDs)) {
          recipients = recipients.filter(entry => (a => a.reduce((b, v) => b && v, true))([
            !departmentIDs || (Array.isArray(entry.departments)
              ? entry.departments.find(({ id }) => departmentIDs.includes(id))
              : false),
            !userIDs || userIDs.includes(entry.id),
          ]));
        }
      }

      if (filterWhere) {
        recipients = recipients.filter(entry => (a => a.reduce((b, v) => b && v, true))([
          !filterWhere.name || `${entry.name}`.toLowerCase().includes(`${filterWhere.name}`.toLowerCase()),
          !filterWhere.emailAddress || `${entry.emailAddress}`.toLowerCase().includes(`${filterWhere.emailAddress}`.toLowerCase()),
          !filterWhere.role || entry.role === filterWhere.role,
          !filterWhere.title || entry.title === filterWhere.title,
          !filterWhere.department || (Array.isArray(entry.departments)
            ? entry.departments.find(({ id }) => filterWhere.department === id)
            : false),
          // @TODO Custom field filter
        ]));
      }

      return recipients;
    },
    searchFields() {
      const { userRoleEnums } = this;
      const roles = Object.keys(userRoleEnums);

      const { departments } = Array.isArray(this.recipients)
        ? this.recipients.reduce((results, entry) => {
          if (!roles.includes('INVITED')) {
            roles.push('INVITED');
          }

          if (Array.isArray(entry.departments)) {
            entry.departments.forEach(({ id, name }) => {
              if (!results.departments.find(d => d.id === id)) {
                results.departments.push({ id, name });
              }
            });
          }

          return results;
        }, {
          departments: [],
        })
        : {};

      return {
        name: {
          label: 'Name',
          placeholder: 'First or Last Name...',
          type: 'STRING',
        },
        emailAddress: {
          label: 'Email Address',
          placeholder: '...@example.com',
          type: 'STRING',
        },
        role: {
          label: 'Role',
          type: 'ENUM',
          options: Array.isArray(roles)
            ? roles.map(value => ({ text: userRoleEnums[value] || _startCase(`${value}`.toLowerCase()), value }))
            : [],
          empty: 'All',
        },
        title: {
          label: 'Title',
          placeholder: 'Example Associate',
          type: 'STRING',
        },
        department: {
          label: 'Department',
          type: 'ENUM',
          options: _sortBy(departments, 'name').map(({ id, name }) => ({ text: name, value: id })),
          disabled: departments.length === 0,
          empty: 'All',
        },
        // ...(Array.isArray(this.userSearchFields) ? this.userSearchFields : []).reduce((r, field) => {
        //   const { key, name: label, type } = field;
        //   const options = type === 'ENUM' ? JSON.parse(field.options) : null;
        //   // if (Array.isArray(options)) {
        //   //   options = [ { text: '--', value: null } ].concat(options);
        //   // }
        //   r[`customField-${key}`] = { label, type, options, empty: '--' };
        //   return r;
        // }, {}),
      };
    },
    isAllSelected() {
      if (Array.isArray(this.selectedItems) && this.selectedItems.length === this.items.length) {
        return this.items.filter(({ __typename, id }) => !this.selectedItems.includes(`${__typename}:${id}`)).length === 0;
      } else {
        return false;
      }
    },
  },
  methods: {
    toggleSelected(itemID) {
      const i = this.selectedItems.indexOf(itemID);
      if (i >= 0) {
        this.selectedItems.splice(i, 1);
      } else {
        this.selectedItems.push(itemID);
      }
    },
    toggleAllSelected() {
      if (Array.isArray(this.recipients) && !this.isAllSelected) {
        this.selectedItems = this.items.map(({ __typename, id }) => `${__typename}:${id}`);
      } else {
        this.selectedItems = [];
      }
    },
    async preview() {
      this.$bvModal.show('previewModal');

      this.previewState = 'LOADING';
      this.emailHTML = '';

      try {
        const { data: emailData } = await this.$apollo.query({
          query: gql`${emailQuery}`,
          fetchPolicy: 'network-only',
        });

        const { data: { renderEmail } } = await this.$apollo.mutate({
          mutation: renderEmailMutation,
          variables: {
            template: this.getEmailBody(),
            data: JSON.stringify(emailData),
          },
        });

        const { body } = renderEmail;
        this.previewState = null;
        this.emailHTML = body;
      } catch (error) {
        this.previewState = 'ERROR';
        this.$log.error(error);
      }
    },
    async sendTestEmail() {
      // TODO: add loading logic and success message loading
      this.isLoading = true;
      try {
        const { user: { id: userID }, fromName, campaign } = this;
        // Manually check we have a userID to send to, otherwise we risk sending to everyone
        this.$assert(userID, new Error('Missing userID'));

        const email = {
          template: this.getEmailBody(),
          query: emailQuery,
          where: { users: { id: userID } },
        };

        if (fromName && fromName.length) {
          email.fromName = fromName;
        }
        if (campaign && campaign.length) {
          email.tags = { ...email.tags, campaign };
        }

        await this.$apollo.mutate({
          mutation: SendEmailMutation,
          variables: { email },
        });
        this.isLoading = false;
        setTimeout(() => this.$refs.testEmailSentSucessfullyModal.show(), 300);
      } catch (err) {
        this.$log.error(err);
        this.isLoading = false;
        setTimeout(() => this.$refs.emailSendErrorModal.show(), 300);
      }
    },
    async sendEmails() {
      const where = {};

      try {
        this.isLoading = true;

        this.selectedItems.forEach(item => {
          const [ __typename, id ] = item.split(':');
          switch (__typename) {
            case 'User':
              where.users = (Array.isArray(where.users) ? where.users : []).concat(id); break;
            case 'UserInvite':
              where.userInvites = (Array.isArray(where.userInvites) ? where.userInvites : []).concat(id);
              break;
          }
        });

        this.$assert(where.users || where.userInvites, 'Expected a user or an invite to be selected');
        this.$assert(!where.users || (Array.isArray(where.users) && where.users.length),
          'Expected users to be an array');
        this.$assert(!where.userInvites || (Array.isArray(where.userInvites) && where.userInvites.length),
          'Expected userInvites to be an array');

        if (Array.isArray(where.users) && where.users.length) {
          where.users = { id_in: where.users };
        }
        if (Array.isArray(where.userInvites) && where.userInvites.length) {
          where.userInvites = { id_in: where.userInvites };
        }

        const email = {
          template: this.getEmailBody(),
          query: emailQuery,
          where,
        };

        const { fromName, campaign } = this;
        if (fromName && fromName.length) {
          email.fromName = fromName;
        }
        if (campaign && campaign.length) {
          email.tags = { ...email.tags, campaign };
        }

        await this.$apollo.mutate({
          mutation: SendEmailMutation,
          variables: { email },
        });

        this.isLoading = false;
        this.$nextTick(() => this.$refs.emailSentSucessfullyModal.show());
      } catch (err) {
        this.$log.error(err);
        this.isLoading = false;
        this.$nextTick(() => this.$refs.emailSendErrorModal.show());
      }
    },
    getEmailBody() {
      const { subject, slots, brandingSettings, segmentWhere, usersContext } = this;

      const params = {
        subject,
        slots,
        brandingSettings,
      };

      if (usersContext === 'LEADERBOARDS' && typeof segmentWhere === 'string') {
        const leaderboard = this.leaderboards.find(l => l.id === segmentWhere);
        this.$assert(leaderboard && leaderboard.id, 'Leaderboard not found');
        params.leaderboard = leaderboard;
      }

      const key = `${this.templateKey}`.startsWith('saved:') ? 'generic' : this.templateKey;
      const template = templateList.find(el => el.value === key);
      this.$assert(template && typeof template.mjml === 'function', 'Template not found');

      return template.mjml.call(this, params);
    },
    showLinkPrompt(command) {
      const href = prompt('Enter the url of your link here');
      if (href !== null) {
        command({ href });
      }
    },
    showImagePrompt(command) {
      const src = prompt('Enter the url of your image here');
      if (src !== null) {
        command({ src });
      }
    },
    async saveNewEmailTemplate() {
      const name = window.prompt('Save with what name?', 'Email template');
      if (!(typeof name === 'string' && name.length)) {
        return;
      }

      try {
        const key = slugify(name, { lower: true, strict: true });

        this.$assert(!this.emailManualTemplates || !this.emailManualTemplates[key],
          new Error(`Template ${key} already exists - please choose another name`));

        const { subject, fromName, campaign, slots, brandingSettings } = this;
        this.emailManualTemplates = {
          ...this.emailManualTemplates,
          [key]: { name, subject, fromName, campaign, slots, brandingSettings },
        };

        await this.$apollo.mutate({
          mutation: saveEmailTemplates,
          variables: { templates: { [key]: JSON.stringify(this.emailManualTemplates[key]) } },
        });

        this.$notification({
          message: `Template "${name}" saved successfully`,
          variant: 'success',
        });
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
      }
    },
    async saveExistingEmailTemplate() {
      try {
        this.$assert(`${this.templateKey}`.startsWith('saved:'), new Error('Expected templateKey to be a saved key'));

        const key = this.templateKey.substr('saved:'.length);
        this.$assert(this.emailManualTemplates[key], new Error(`Expected template ${key} to already exists`));

        const { name } = this.emailManualTemplates[key];
        const { subject, fromName, campaign, slots, brandingSettings } = this;
        this.emailManualTemplates = {
          ...this.emailManualTemplates,
          [key]: { name, subject, fromName, campaign, slots, brandingSettings },
        };

        await this.$apollo.mutate({
          mutation: saveEmailTemplates,
          variables: { templates: { [key]: JSON.stringify(this.emailManualTemplates[key]) } },
        });

        this.$notification({
          message: `Template "${name}" updated successfully`,
          variant: 'success',
        });
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
      }
    },
    async removeSavedEmailTemplate() {
      try {
        this.$assert(`${this.templateKey}`.startsWith('saved:'), new Error('Expected templateKey to be a saved key'));

        const key = this.templateKey.substr('saved:'.length);
        this.$assert(this.emailManualTemplates[key], new Error(`Expected template ${key} to already exists`));

        const { name, template } = this.emailManualTemplates[key];

        await this.$apollo.mutate({
          mutation: saveEmailTemplates,
          variables: { templates: { [key]: null } },
        });

        this.templateKey = template;

        if (this.emailManualTemplates) {
          // Deliberately with an equals to trigger computed changes
          this.emailManualTemplates = { ...this.emailManualTemplates, [key]: undefined };
          delete this.emailManualTemplates[key];
        }

        this.$notification({
          message: `Template "${name}" deleted successfully`,
          variant: 'success',
        });
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
      }
    },
  },
  watch: {
    company() {
      this.subject = `A message from ${this.company.brand.name}`;
    },
    templateKey(value, oldValue) {
      let template = null;
      if (`${value}`.startsWith('saved:')) {
        const { fromName, subject, campaign, slots, brandingSettings } = this.emailManualTemplates[value.substr('saved:'.length)] || {};
        template = templateList.find(el => el && el.value === 'generic');

        this.subject = subject || template.subject || null;
        this.brandingSettings = { ...this.defaultBrandingSettings, ...brandingSettings };
        this.slots = Array.isArray(slots) ? slots : (Array.isArray(template.slots) ? template.slots : []).map(el => ({
          name: el.name,
          type: el.type,
          content: el.defaultContent,
        }));

        this.fromName = fromName || null;
        this.campaign = campaign || null;
      } else {
        template = templateList.find(el => el && el.value === value) || {};

        this.subject = template.subject || '';
        this.brandingSettings = { ...this.defaultBrandingSettings };
        this.slots = (Array.isArray(template.slots) ? template.slots : []).map(el => ({
          name: el.name,
          type: el.type,
          content: el.defaultContent,
        }));

        if (`${oldValue}`.startsWith('saved:')) {
          this.fromName = null;
          this.campaign = null;
        }
      }

      this.usersContext = template.usersContext || null;

      this.selectedItems = [];
    },
    usersContext(value) {
      if (value === 'LEADERBOARDS') {
        this.segmentSelectorOpts = this.leaderboards.map(({ id, name }) => ({ text: name, value: id }));
        this.segmentWhere = this.leaderboards.slice(0, 1).shift().id;
      } else {
        this.segmentSelectorOpts = DEFAULT_SEGMENT_OPTS;
        this.segmentWhere = null;
      }
    },
    segmentWhere() {
      this.selectedItems = [];
    },
  },
};

const DEFAULT_SEGMENT_OPTS = [
  {
    text: 'Users & Invited Users',
    value: null,
  },
  {
    text: 'All Users',
    value: {
      includeUsers: true,
      whereUsers: { status: 'ACTIVE' },
    },
  },
  {
    text: 'All Invites',
    value: {
      includeInvites: true,
      whereInvites: { status: 'INVITED' },
    },
  },
  {
    text: 'Users - Synced',
    value: {
      includeUsers: true,
      whereUsers: {
        logsBy: {
          eventName: 'CONTACT_NETWORK_SYNCED'
        },
        status: 'ACTIVE',
      },
    },
  },
  {
    text: 'Users - Not-synced',
    value: {
      includeUsers: true,
      whereUsers: {
        logsBy: {
          NOT: {
            eventName: 'CONTACT_NETWORK_SYNCED'
          },
        },
        status: 'ACTIVE',
      },
    },
  },
  {
    text: 'Users - Have Referred/Shared',
    value: {
      includeUsers: true,
      whereUsers: {
        logsBy: {
          OR: [
            { eventName: 'JOB_SHARED' },
            { eventName: 'REFERRAL_SUGGESTED' },
            { eventName: 'REFERRAL_ACCEPTED' },
          ],
        },
        status: 'ACTIVE',
      },
    },
  },
  {
    text: 'Users - Have Not Referred/Shared',
    value: {
      includeUsers: true,
      whereUsers: {
        logsBy: {
          OR: [
            { eventName: 'JOB_SHARED' },
            { eventName: 'REFERRAL_SUGGESTED' },
            { eventName: 'REFERRAL_ACCEPTED' },
          ],
        },
        status: 'ACTIVE',
      },
    },
  },
];
</script>

<style lang="scss">

  .feedback-icon-line {
    font-size: 60px;
    text-align: center;

    &.success {
      color: #27ae60;
    }

    &.error {
      color: #e74c3c;
    }
  }

  .feedback-text-line {
    font-size: 14px;
    text-align: center;
  }

  .manage-emailsender {

    img {
      max-width: 380px !important;
      max-height: 290px !important;
    }

    .variables-box {
      span {
        display: block;
        width: 100%;
      }
    }

    .email-tag {
      display: inline-block;
      padding: 3px 8px;
      background: #eee;
      border-radius: 12px;
      margin: 5px 5px 0px 0px;

      .remove-email-button {
        display: inline-block;
        color: #fff;
        margin-left: 7px;
        font-weight: bold;
        font-size: 8px;
        line-height: 14px;
        height: 14px;
        width: 14px;
        border-radius: 7px;
        text-align: center;
        background: #7f8c8d;
        cursor: pointer;
        position: relative;
        bottom: 1px;
      }
      .remove-email-button:active {
        bottom: 0px;
      }
    }

    .email-tag-expand {
      display: inline-block;
      padding: 3px 0px;
      margin: 5px 3px 0px 3px;
      color: #0284FF;
      text-decoration: underline;
      cursor: pointer;
    }

    .to-line-placeholder {
      display: inline-block;
      margin-top: 8px;
      color: #666;
    }

    * {
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important;

      &:focus {
        outline: none !important;
      }
    }
  }

  #previewModal {
    .preview-iframe {
      border: 0px solid #666;
      border-radius: 6px;
      box-shadow: 0 0 12px rgba(0, 0, 0, .3)
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/Recruiter/EmailSender.vue